/**
 * WatchpointEditDialog.tsx — edit an existing watchpoint in a dialog.
 * Type is IMMUTABLE (backend does not accept it on PUT); shown read-only.
 * Config is seeded directly from watchpoint.config (not masked/encrypted).
 */
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Button,
  Input,
  Badge,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { Alert } from '@vnetwork/ui/feedback';
import { useUpdateWatchpoint, useAlarmFieldCatalogByType, type Watchpoint } from '@/api/healthcheck';
import { hcExtractError } from '@/api/healthcheck';
import { typeMeta } from '../shared';
import { WatchpointConfigForm, isConfigValid, applyConfigDefaults, type ConfigState } from './WatchpointConfigForms';

interface Props {
  watchpoint: Watchpoint | null;
  projectId: string;
  onClose: () => void;
}

export function WatchpointEditDialog({ watchpoint, projectId, onClose }: Props) {
  return (
    <Dialog open={!!watchpoint} onOpenChange={(open) => !open && onClose()}>
      <DialogContent size="lg">
        {watchpoint ? (
          <WatchpointEditForm watchpoint={watchpoint} projectId={projectId} onClose={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function WatchpointEditForm({
  watchpoint: wp,
  projectId,
  onClose,
}: {
  watchpoint: Watchpoint;
  projectId: string;
  onClose: () => void;
}) {
  const meta = typeMeta(wp.type);

  // Seed state from the loaded watchpoint
  const [name, setName] = useState(wp.name);
  const [config, setConfig] = useState<ConfigState>({ ...wp.config });
  const [interval, setIntervalSec] = useState(wp.check_interval);
  const [timeout, setTimeoutSec] = useState(wp.timeout_seconds);
  const [showErrors, setShowErrors] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const update = useUpdateWatchpoint(wp.id, projectId, { onSuccess: onClose });
  const { data: catalog } = useAlarmFieldCatalogByType(
    wp.type === 'waap' || wp.type === 'delivery' ? wp.type : undefined,
  );

  const nameOk = name.trim().length >= 3;
  const configOk = isConfigValid(wp.type, config, catalog?.breakdown_metrics ?? []);

  const validate = (): boolean => {
    setShowErrors(true);
    return nameOk && configOk && interval >= 10 && timeout >= 5;
  };

  const handleSave = () => {
    setSubmitError(null);
    if (!validate()) return;

    // Only send fields that differ (omit unchanged) — PATCH-like behaviour on PUT
    const finalConfig = applyConfigDefaults(wp.type, config);
    const input: Record<string, unknown> = {};
    if (name.trim() !== wp.name) input.name = name.trim();
    if (JSON.stringify(finalConfig) !== JSON.stringify(wp.config)) input.config = finalConfig;
    if (interval !== wp.check_interval) input.check_interval = interval;
    if (timeout !== wp.timeout_seconds) input.timeout_seconds = timeout;

    // Always send at least one field (name fallback ensures non-empty payload)
    if (Object.keys(input).length === 0) {
      onClose();
      return;
    }

    update.mutate(input, {
      onError: (e) => setSubmitError(hcExtractError(e, 'Update failed')),
    });
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          Edit monitor —{' '}
          <Badge tone="neutral" appearance="subtle" size="sm" style={{ marginLeft: 6 }}>
            {meta.label}
          </Badge>
        </DialogTitle>
      </DialogHeader>

      <DialogBody>
        <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
          {/* Type — read-only (immutable after create) */}
          <Field label="Type">
            {() => (
              <div
                style={{
                  padding: 'var(--vn-space-2) var(--vn-space-3)',
                  borderRadius: 'var(--vn-radius-md)',
                  background: 'var(--vn-bg-subtle)',
                  color: 'var(--vn-fg-muted)',
                  fontSize: 'var(--vn-font-size-sm)',
                  border: '1px solid var(--vn-border-default)',
                }}
              >
                {meta.label}
                <span
                  style={{ marginLeft: 8, fontSize: 'var(--vn-font-size-xs)', opacity: 0.7 }}
                >
                  (cannot be changed)
                </span>
              </div>
            )}
          </Field>

          {/* Name */}
          <Field
            label="Name"
            required
            error={showErrors && !nameOk ? 'At least 3 characters.' : undefined}
          >
            {(p) => (
              <Input
                {...p}
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={255}
                invalid={showErrors && !nameOk}
                style={{ maxWidth: 420 }}
              />
            )}
          </Field>

          {/* Per-type config (reuse the controlled config form) */}
          <div>
            <p
              style={{
                margin: '0 0 var(--vn-space-2)',
                fontWeight: 600,
                fontSize: 'var(--vn-font-size-sm)',
              }}
            >
              {meta.label} configuration
            </p>
            <WatchpointConfigForm
              type={wp.type}
              config={config}
              onChange={(patch) => setConfig((c) => ({ ...c, ...patch }))}
              showErrors={showErrors}
            />
          </div>

          {/* Schedule */}
          <div>
            <p
              style={{
                margin: '0 0 var(--vn-space-2)',
                fontWeight: 600,
                fontSize: 'var(--vn-font-size-sm)',
              }}
            >
              Schedule
            </p>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 'var(--vn-space-3)',
                maxWidth: 420,
              }}
            >
              <Field
                label="Interval (seconds)"
                error={showErrors && interval < 10 ? 'Min 10 seconds.' : undefined}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    min={10}
                    value={String(interval)}
                    onChange={(e) => setIntervalSec(Math.max(10, Number(e.target.value) || 10))}
                    invalid={showErrors && interval < 10}
                  />
                )}
              </Field>
              <Field
                label="Timeout (seconds)"
                error={showErrors && timeout < 5 ? 'Min 5 seconds.' : undefined}
              >
                {(p) => (
                  <Input
                    {...p}
                    type="number"
                    min={5}
                    value={String(timeout)}
                    onChange={(e) => setTimeoutSec(Math.max(5, Number(e.target.value) || 5))}
                    invalid={showErrors && timeout < 5}
                  />
                )}
              </Field>
            </div>
          </div>

          {submitError && <Alert tone="danger" title={submitError} />}
        </div>
      </DialogBody>

      <DialogFooter>
        <Button variant="ghost" onClick={onClose} disabled={update.isPending}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </DialogFooter>
    </>
  );
}
